/**
 * KAN-862: канбан-доска CRM (GET /api/v1/crm/pipeline/).
 * desktop: колонки рядом (~280px) с горизонтальной прокруткой, карточки
 * перетаскиваются между колонками (@hello-pangea/dnd), перенос оптимистичный.
 * mobile: колонка на ширину экрана, горизонтальный scroll-snap, сверху вкладки
 * колонок со счётчиками; этап меняется кнопкой "Сменить этап" в карточке.
 * KAN-865: клик по карточке открывает детали (onOpenCard); attentionOnly -
 * только требующие внимания (?attention=1); число требующих внимания в шапке.
 * desktop: доска занимает экран до низа (useFillViewportHeight, режим height),
 * колонки на всю высоту, горизонтальная полоса прокрутки видна внизу экрана;
 * кнопка "Развернуть"/"Свернуть" как у таблицы CRM (useFullscreenToggle).
 * KAN-875: mobile-карусель в браузере компьютера листается колесом,
 * перетаскиванием мышью и стрелками (useHorizontalScroll, snap сохраняется).
 * KAN-880: filters (поиск, менеджер, без движения) уходят в запросы доски и
 * порций колонок; под фильтром никого нет - "Ничего не найдено".
 * KAN-882: доска в 4 видах (board): колонки только из columns ответа.
 * desktop: перенос во всех видах, зона переноса по accepts колонки, решение -
 * useCrmBoardMoves (accepts/target). mobile: вкладки в порядке mobileTabOrder
 * (свёрнутые в конце), по умолчанию defaultColumnKey (KAN-886: "Требуют
 * внимания"), выбор сбрасывается при смене вида; "Сменить этап" только в виде с колонками этапов.
 * KAN-957: desktop-доска снимает нижний отступ кабинета под кнопку видео-подсказки
 * (useReleaseBottomGutter) и тянется до низа экрана; в конце ряда колонок место под
 * плавающую кнопку. toolbar (панель фильтров секции) рисуется внутри развёрнутой доски.
 */
import type { CrmRange } from '@/lib/crmTable';
import { useMemo, useState, type ReactNode } from 'react';
import { DragDropContext } from '@hello-pangea/dnd';
import { Loader2, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AttentionDoneDialog } from '@/components/dashboard/AttentionDoneDialog';
import { useReleaseBottomGutter } from '@/components/layout/bottomGutter';
import { isNotFoundError } from '@/hooks/useCatalog';
import { useCrmBoardMoves } from '@/hooks/useCrmBoardMoves';
import { crmPipelineKey, useCrmPipeline } from '@/hooks/useCrmPipeline';
import { useFillViewportHeight } from '@/hooks/useFillViewportHeight';
import { useCrmFullscreen } from '@/hooks/useCrmFullscreen';
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll';
import { useSnapColumnTabs } from '@/hooks/useSnapColumnTabs';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { hasCrmLeadFilters } from '@/lib/crmLeadFilters';
import {
  boardChangesStage,
  boardHasAttentionFilter,
  boardShowsCardState,
  buildApiColumns,
  canDragCard,
  defaultColumnKey,
  mobileTabOrder,
} from '@/lib/crmPipeline';
import { cn } from '@/lib/utils';
import type { WorkSurface } from '@/lib/workActions';
import type { CrmBoardColumn, CrmBoardView, CrmLeadFilterParams, CrmPipelineCard } from '@/types/crmPipeline';
import { KanbanColumn, type KanbanCardLinks } from './KanbanColumn';
import { KanbanColumnTabs } from './KanbanColumnTabs';
import { ScrollArrows } from './ScrollArrows';
import { StagePicker } from './StagePicker';

export interface CrmKanbanBoardProps {
  /** KAN-882: вид доски (?board=). */
  board: CrmBoardView;
  activity: CrmRange | null;
  variant: 'desktop' | 'mobile';
  clientHref: (card: CrmPipelineCard) => string;
  companyHref?: (companyId: string) => string;
  /** KAN-865: открыть детали клиента по клику на карточку. */
  onOpenCard?: (card: CrmPipelineCard) => void;
  /** KAN-865: только клиенты, которые требуют внимания. */
  attentionOnly?: boolean;
  /** KAN-880: фильтры поиска, менеджера и "без движения" (только заданные). */
  filters?: CrmLeadFilterParams;
  /** KAN-880: поверхность "Взять в работу" при переносе (кабинет или приложение). */
  surface?: WorkSurface;
  /** KAN-883: выбранная вкладка-колонка телефона снаружи (переживает перезапрос и смену фильтров). */
  columnChoice?: string | null;
  onColumnChoice?: (key: string) => void;
  /** KAN-957: панель фильтров внутри развёрнутой доски (desktop). */
  toolbar?: ReactNode;
}

const NO_FILTERS: CrmLeadFilterParams = {};

/** KAN-766/865: развёрнутая доска: весь экран, выше шапки и сайдбара, ниже модалок (z-50). */
const FULLSCREEN_BOARD = 'fixed inset-0 z-40 flex flex-col bg-white p-3';
/** Высота доски до низа экрана; развёрнутая тянется до самого низа и на низком экране. */
const NORMAL_FILL = { mode: 'height' } as const;
// bottomOffset 12: нижний padding развёрнутой доски (p-3), полоса прокрутки не залезает на отступ.
const FULLSCREEN_FILL = { mode: 'height', bottomOffset: 12, smallScreenHeight: 0 } as const;

function AttentionCount({ count }: { count: number | undefined }) {
  if (!count || count <= 0) return null;
  return (
    <span
      data-testid="crm-board-attention-count"
      title="Требуют внимания"
      className="rounded-full bg-rose-600 px-2 py-0.5 text-xs font-semibold text-white"
    >
      {count} ждут
    </span>
  );
}

/** Горизонтальный ряд колонок высотой до низа экрана (desktop). */
function DesktopScroller({ expanded, children }: { expanded: boolean; children: (fill: boolean) => ReactNode }) {
  // KAN-957: до хука высоты, иначе первый расчёт вычтет отступ под кнопку подсказки.
  useReleaseBottomGutter();
  const fill = useFillViewportHeight<HTMLDivElement>(expanded ? FULLSCREEN_FILL : NORMAL_FILL);
  return (
    <div
      ref={fill.ref}
      style={fill.style}
      data-testid="crm-board-scroller"
      className="flex min-h-0 items-stretch gap-3 overflow-x-auto overflow-y-hidden pb-3"
    >
      {children(fill.height !== null)}
      {/* KAN-957: место под плавающую кнопку подсказки справа внизу: последняя колонка не под ней. */}
      <div aria-hidden="true" data-testid="crm-board-button-room" className="w-56 shrink-0" />
    </div>
  );
}

export function CrmKanbanBoard({
  board,
  activity,
  variant,
  clientHref,
  companyHref,
  onOpenCard,
  attentionOnly = false,
  filters = NO_FILTERS,
  surface = 'cabinet',
  columnChoice,
  onColumnChoice,
  toolbar,
}: CrmKanbanBoardProps): JSX.Element {
  const { data, isLoading, error } = useCrmPipeline(board, activity, attentionOnly, filters);
  const fullscreen = useCrmFullscreen();
  const [picking, setPicking] = useState<CrmPipelineCard | null>(null);
  const mobile = variant === 'mobile';
  const apiColumns = useMemo(() => (data ? buildApiColumns(data) : []), [data]);
  const columns = useMemo(() => (mobile ? mobileTabOrder(apiColumns) : apiColumns), [apiColumns, mobile]);
  const moves = useCrmBoardMoves(board, columns, crmPipelineKey(board, activity, attentionOnly, filters), surface);
  const tabs = useSnapColumnTabs(
    columns.map((c) => c.key),
    mobile,
    { defaultKey: defaultColumnKey(columns), resetKey: board, choice: columnChoice, onChoiceChange: onColumnChoice }
  );
  const carousel = useHorizontalScroll<HTMLDivElement>({ externalRef: tabs.scrollerRef, wheelPaging: true });

  const expanded = !mobile && fullscreen.isFullscreen;
  const toggleLabel = expanded ? 'Свернуть' : 'Развернуть';
  const ToggleIcon = expanded ? Minimize2 : Maximize2;
  // Обёртка desktop-доски: панель фильтров (в развёрнутом виде), шапка, содержимое.
  // KAN-957: та же обёртка и при загрузке/ошибке в развёрнутом виде: панель не размонтируется.
  const desktopShell = (attention: ReactNode, body: ReactNode) => (
    <div
      data-testid="crm-kanban-board"
      data-variant="desktop"
      data-fullscreen={String(expanded)}
      className={cn('min-w-0', expanded && FULLSCREEN_BOARD)}
    >
      {expanded && toolbar ? <div className="mb-2 flex min-w-0 flex-col gap-2">{toolbar}</div> : null}
      <div className="mb-2 flex min-w-0 items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">{attention}</div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-gray-500"
          aria-label={toggleLabel}
          title={toggleLabel}
          aria-pressed={expanded}
          data-testid="crm-board-fullscreen-toggle"
          onClick={fullscreen.toggle}
        >
          <ToggleIcon className="h-4 w-4" />
        </Button>
      </div>
      {body}
    </div>
  );

  let status: JSX.Element | null = null;
  if (isLoading) {
    status = (
      <div className="flex justify-center py-10" data-testid="crm-board-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-label="Загрузка" />
      </div>
    );
  } else if (isNotFoundError(error)) {
    status = (
      <div className="rounded-lg border bg-white p-6 text-sm text-gray-600" data-testid="crm-board-not-ecommerce">
        CRM доступна только в режиме интернет-магазина.
      </div>
    );
  } else if (error || !data) {
    status = (
      <div className="rounded-lg border bg-white p-6 text-sm text-red-600" data-testid="crm-board-error">
        Не удалось загрузить доску: {extractErrorMessage(error, 'ошибка сервера')}
      </div>
    );
  }
  if (status || !data) return expanded ? desktopShell(null, status) : (status ?? <></>);

  const links: KanbanCardLinks = {
    clientHref,
    companyHref,
    onChangeStage: mobile && boardChangesStage(columns) ? setPicking : undefined,
    onOpen: onOpenCard,
    showState: boardShowsCardState(board),
  };
  const canDrag = (column: CrmBoardColumn) => (card: CrmPipelineCard) => canDragCard(card, column, columns);
  const renderColumn = (column: CrmBoardColumn, className: string, dnd: boolean, fill = false) => (
    <KanbanColumn
      key={column.key}
      board={board}
      column={column}
      canDrag={canDrag(column)}
      activity={activity}
      attentionOnly={attentionOnly}
      filters={filters}
      variant={variant}
      fill={fill}
      dnd={dnd}
      className={className}
      {...links}
    />
  );
  const showAttention = boardHasAttentionFilter(board) && !!data.attention_count;
  const attentionCount = showAttention ? <AttentionCount count={data.attention_count} /> : null;
  const doneDialog = (
    <AttentionDoneDialog
      item={moves.done.dialogItem}
      defaultManager={moves.done.defaultManager}
      onConfirm={moves.done.confirm}
      onCancel={moves.done.cancel}
    />
  );
  // Под фильтром никого нет: заглушка внутри обычной обёртки доски (полноэкранный режим и шапка на месте).
  const nothingFound = hasCrmLeadFilters(filters) && columns.every((c) => c.count === 0);
  const emptyNote = (
    <p data-testid="crm-board-empty" className="w-full rounded-lg border bg-white py-10 text-center text-sm text-gray-500">
      Ничего не найдено
    </p>
  );

  if (mobile) {
    return (
      <div data-testid="crm-kanban-board" data-variant="mobile" className="min-w-0">
        {showAttention ? <div className="mb-2">{attentionCount}</div> : null}
        {nothingFound && emptyNote}
        <div className={cn(nothingFound && 'hidden')}>
        <KanbanColumnTabs columns={columns} active={tabs.active} onSelect={tabs.selectTab} />
        <div className="relative -mx-4">
          <div
            ref={carousel.ref}
            data-testid="kanban-column-carousel"
            className="relative flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-4 pb-2 [scrollbar-width:none]"
          >
            {columns.map((column) => renderColumn(column, 'w-[calc(100vw-2rem)] max-w-[34rem] snap-center', false))}
          </div>
          <ScrollArrows
            canScrollLeft={carousel.canScrollLeft}
            canScrollRight={carousel.canScrollRight}
            onScroll={carousel.scrollPage}
          />
        </div>
        </div>
        {columns.length === 0 && <p className="py-10 text-center text-sm text-gray-500">Клиентов нет</p>}
        <StagePicker
          card={picking}
          stages={data.stages}
          onClose={() => setPicking(null)}
          onSelect={(card, stageId) => {
            setPicking(null);
            moves.changeStage(card, stageId, data.stages);
          }}
        />
        {doneDialog}
      </div>
    );
  }

  const content = (
    <DesktopScroller expanded={expanded}>
      {(fill) => (
        <>
          {nothingFound ? emptyNote : columns.map((column) => renderColumn(column, 'w-[280px]', true, fill))}
          {columns.length === 0 && <p className="py-10 text-sm text-gray-500">Клиентов нет</p>}
        </>
      )}
    </DesktopScroller>
  );
  return desktopShell(
    attentionCount,
    <>
      <DragDropContext onDragEnd={moves.onDragEnd}>{content}</DragDropContext>
      {doneDialog}
    </>
  );
}
